import React, { useState, useEffect, useRef } from 'react';
import { DataTable, DataTableExpandedRows, DataTableRowEvent, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { Menubar } from 'primereact/menubar';
import { Panel } from 'primereact/panel';
import { ProgressBar } from 'primereact/progressbar';
import { Message } from 'primereact/message';
import { customersApi } from '../../api';
import { Customer, Check, CustomerDeposit } from '../../types';
import { useDataTableState } from '../../hooks';
import { formatDate, formatCurrency } from '../../utils';

interface CustomersTabProps {
  clientId: string;
}

export const CustomersTab: React.FC<CustomersTabProps> = ({ clientId }) => {
  const toast = useRef<Toast>(null);
  const tableRef = useRef<DataTable<Customer[]>>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [sourceCustomer, setSourceCustomer] = useState<Customer | null>(null);
  const [targetCustomer, setTargetCustomer] = useState<Customer | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [editName, setEditName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Row expansion state
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>();
  const [checkMap, setCheckMap] = useState<Record<string, Check[]>>({});
  const [depositMap, setDepositMap] = useState<Record<string, CustomerDeposit[]>>({});
  const [expansionLoading, setExpansionLoading] = useState<Record<string, boolean>>({});

  // Persistent filter and sort state
  const {
    sortField,
    sortOrder,
    handleSort,
    filters,
    handleFilter: onFilterChange,
  } = useDataTableState({
    storageKey: 'customerIdentification:customers',
    defaultSort: { field: 'name', order: 1 },
    defaultFilters: {
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  useEffect(() => {
    loadCustomers();
  }, [clientId]);


  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      const data = await customersApi.listCustomers(clientId);
      setCustomers(data.filter((c) => !c.is_merged));
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load customers',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Customer name is required',
        life: 3000,
      });
      return;
    }

    try {
      setIsCreating(true);
      const created = await customersApi.createCustomer(clientId, {
        name: newCustomerName,
      });
      setCustomers([created, ...customers]);
      setShowCreateDialog(false);
      setNewCustomerName('');
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Customer created successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to create customer',
        life: 5000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleMergeCustomers = async () => {
    if (!sourceCustomer || !targetCustomer) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select both source and target customers',
        life: 3000,
      });
      return;
    }

    if (sourceCustomer.id === targetCustomer.id) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Cannot merge a customer with itself',
        life: 3000,
      });
      return;
    }

    try {
      setIsMerging(true);
      await customersApi.mergeCustomers(clientId, sourceCustomer.id, targetCustomer.id);
      setCustomers(customers.filter((c) => c.id !== sourceCustomer.id));
      // Clear cached data and collapse expanded rows so fresh data is fetched on next expand
      setCheckMap((prev) => {
        const updated = { ...prev };
        delete updated[sourceCustomer.id];
        delete updated[targetCustomer.id];
        return updated;
      });
      setDepositMap((prev) => {
        const updated = { ...prev };
        delete updated[sourceCustomer.id];
        delete updated[targetCustomer.id];
        return updated;
      });
      setExpandedRows({});
      setShowMergeDialog(false);
      setSourceCustomer(null);
      setTargetCustomer(null);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Customers merged successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to merge customers',
        life: 5000,
      });
    } finally {
      setIsMerging(false);
    }
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setEditName(customer.name);
    setShowEditDialog(true);
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer || !editName.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Customer name is required',
        life: 3000,
      });
      return;
    }

    try {
      setIsUpdating(true);
      const updated = await customersApi.updateCustomer(clientId, editingCustomer.id, { name: editName });
      setCustomers(customers.map((c) => (c.id === updated.id ? updated : c)));
      setShowEditDialog(false);
      setEditingCustomer(null);
      setEditName('');
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Customer updated successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to update customer',
        life: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCustomer = (customer: Customer) => {
    confirmDialog({
      message: `Are you sure you want to delete customer "${customer.name}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await customersApi.deleteCustomer(customer.id);
          setCustomers(customers.filter((c) => c.id !== customer.id));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Customer deleted successfully',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to delete customer',
            life: 5000,
          });
        }
      },
    });
  };

  const actionBodyTemplate = (rowData: Customer) => {
    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-edit"
          link
          tooltip="Edit"
          onClick={() => handleEditCustomer(rowData)}
        />
        <Button
          icon="ms-out ms-out-delete"
          link
          tooltip="Delete"
          onClick={() => handleDeleteCustomer(rowData)}
        />
      </div>
    );
  };

  const handleRowExpand = (event: DataTableRowEvent) => {
    const customer = event.data as Customer;

    if (checkMap[customer.id] || expansionLoading[customer.id]) {
      return;
    }

    setExpansionLoading((prev) => ({ ...prev, [customer.id]: true }));

    Promise.all([
      customersApi.getCustomerChecks(clientId, customer.id),
      customersApi.getCustomerDeposits(clientId, customer.id),
    ])
      .then(([checks, deposits]) => {
        setCheckMap((prev) => ({ ...prev, [customer.id]: checks }));
        setDepositMap((prev) => ({ ...prev, [customer.id]: deposits }));
      })
      .catch((err) => {
        console.error('Failed to fetch data for customer', customer.id, ':', err);
        setCheckMap((prev) => ({ ...prev, [customer.id]: [] }));
        setDepositMap((prev) => ({ ...prev, [customer.id]: [] }));
      })
      .finally(() => {
        setExpansionLoading((prev) => ({ ...prev, [customer.id]: false }));
      });
  };

  const onPage = (e: DataTablePageEvent) => {
    setFirst(e.first);
    setRows(e.rows);
    setTimeout(() => {
      const wrapper = tableRef.current?.getElement()?.querySelector('.p-datatable-wrapper');
      if (wrapper) {
        wrapper.scrollTop = 0;
      }
    }, 0);
  };

  const rowExpansionTemplate = (customer: Customer) => {
    const checks = checkMap[customer.id] || [];
    const deposits = depositMap[customer.id] || [];
    const isLoading = expansionLoading[customer.id];

    if (isLoading) {
      return <ProgressBar mode="indeterminate" />;
    }

    if (checks.length === 0 && deposits.length === 0) {
      return (
        <div className="row-expansion-container">
          <Message severity="info" text="No checks or deposits assigned to this customer." />
        </div>
      );
    }

    return (
      <div className="row-expansion-container">
        {checks.length > 0 && (
          <Panel header="Checks">
            <DataTable value={checks} size="small">
              <Column field="account_name" header="Account" style={{ width: '20%' }} />
              <Column
                field="check_date"
                header="Date"
                style={{ width: '12%' }}
                body={(rowData: Check) => formatDate(rowData.check_date)}
              />
              <Column field="check_number" header="Check #" style={{ width: '10%' }} />
              <Column
                field="amount"
                header="Amount"
                style={{ width: '12%' }}
                body={(rowData: Check) => formatCurrency(rowData.amount)}
              />
              <Column field="payee_name" header="Payee" style={{ width: '20%' }} />
              <Column field="memo" header="Memo" />
            </DataTable>
          </Panel>
        )}

        {deposits.length > 0 && (
          <Panel header="Deposits">
            <DataTable value={deposits} size="small">
              <Column field="account_name" header="Account" style={{ width: '20%' }} />
              <Column
                field="deposit_date"
                header="Date"
                style={{ width: '12%' }}
                body={(rowData: CustomerDeposit) => formatDate(rowData.deposit_date)}
              />
              <Column
                field="amount"
                header="Amount"
                style={{ width: '12%' }}
                body={(rowData: CustomerDeposit) => formatCurrency(rowData.amount)}
              />
              <Column field="customer_name" header="Customer Name" />
            </DataTable>
          </Panel>
        )}
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <Menubar
        className="actions-menubar"
        model={[
          {
            label: 'New Customer',
            icon: 'ms-out ms-out-add',
            command: () => setShowCreateDialog(true),
          },
          {
            label: 'Merge Customers',
            icon: 'ms-out ms-out-merge',
            command: () => setShowMergeDialog(true),
          },
        ]}
      />

      <DataTable
        ref={tableRef}
        value={customers}
        dataKey="id"
        loading={isLoading}
        paginator
        first={first}
        rows={rows}
        onPage={onPage}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> customers
            </span>
          ),
        }}
        size="small"
        stripedRows
        scrollable
        scrollHeight="flex"
        filterDisplay="menu"
        filters={filters}
        onFilter={onFilterChange}
        emptyMessage="No customers found"
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={handleSort}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
        rowExpansionTemplate={rowExpansionTemplate}
        onRowExpand={handleRowExpand}
      >
        <Column expander style={{ width: '3%' }} />
        <Column
          field="name"
          header="Name"
          sortable
          style={{ width: '25%' }} 
          filter 
          filterPlaceholder="Search name" 
          showFilterMatchModes={false} 
        />
        <Column
          field="qbo_customer_id"
          header="In QB?"
          sortable
          style={{ width: '15%' }}
          body={(rowData) => {
            if (rowData.qbo_customer_id) {
              return (
                <i className="ms-out ms-out-check-circle ms-success"></i>
              );
            }
            return <span style={{ color: 'gray' }}>-</span>;
          }}
        />
        <Column
          field="created_at"
          header="Created"
          body={(rowData) => new Date(rowData.created_at).toLocaleDateString()}
          sortable
          style={{ width: '15%' }}
        />
        <Column body={actionBodyTemplate} />
      </DataTable>

      {/* Create Customer Dialog */}
      <Dialog
        header="Create New Customer"
        visible={showCreateDialog}
        className="assign-dialog"
        onHide={() => {
          setShowCreateDialog(false);
          setNewCustomerName('');
        }}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowCreateDialog(false)}
            />
            <Button
              label="Create"
              severity="success"
              onClick={handleCreateCustomer}
              loading={isCreating}
            />
          </div>
        }
      >
        <div className="dialog-form">
          <div className="field">
            <label htmlFor="customerName">Customer Name</label>
            <InputText
              id="customerName"
              value={newCustomerName}
              onChange={(e) => setNewCustomerName(e.target.value)}
              placeholder="Enter customer name"
              className="full-width-input"
              autoFocus
            />
          </div>
        </div>
      </Dialog>

      {/* Merge Customers Dialog */}
      <Dialog
        header="Merge Customers"
        visible={showMergeDialog}
        className="merge-dialog"
        onHide={() => {
          setShowMergeDialog(false);
          setSourceCustomer(null);
          setTargetCustomer(null);
        }}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowMergeDialog(false)}
            />
            <Button
              label="Merge"
              onClick={handleMergeCustomers}
              severity="success"
              loading={isMerging}
              disabled={!sourceCustomer || !targetCustomer}
            />
          </div>
        }
      >
        <div className="dialog-form">
          <div className="field">
            <label>Source Customer (will be merged into target)</label>
            <Dropdown
              value={sourceCustomer}
              options={customers}
              onChange={(e) => setSourceCustomer(e.value)}
              optionLabel="name"
              placeholder="Select source customer"
              className="full-width-input"
              filter
            />
          </div>
          <div className="field">
            <label>Target Customer (will remain after merge)</label>
            <Dropdown
              value={targetCustomer}
              options={customers}
              onChange={(e) => setTargetCustomer(e.value)}
              optionLabel="name"
              placeholder="Select target customer"
              className="full-width-input"
              filter
            />
          </div>
        </div>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog
        header="Edit Customer"
        visible={showEditDialog}
        className="assign-dialog"
        onHide={() => {
          setShowEditDialog(false);
          setEditingCustomer(null);
          setEditName('');
        }}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowEditDialog(false)}
            />
            <Button
              label="Save"
              severity="success"
              onClick={handleUpdateCustomer}
              loading={isUpdating}
            />
          </div>
        }
      >
        <div className="dialog-form">
          <div className="field">
            <label htmlFor="editCustomerName">Customer Name *</label>
            <InputText
              id="editCustomerName"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter customer name"
              className="full-width-input"
              autoFocus
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};
